import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useIntl } from 'umi';
import {
  createAdmin,
  deleteAdmin,
  getAdminById,
  getAdmins,
  updateAdmin,
} from '@/services/ant-design-pro/api';
import type { API } from '@/services/ant-design-pro/typings';
import { getAuthToken } from '@/utils/authPersistence';

/**
 * Get admins list query hook
 */
export const useAdmins = (params?: API.AdminListParams) => {
  return useQuery({
    queryKey: ['admins', params],
    queryFn: async () => {
      const response = await getAdmins(params);
      return response;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Get single admin by ID query hook
 */
export const useAdmin = (id: string | undefined) => {
  return useQuery({
    queryKey: ['admin', id],
    queryFn: async () => {
      if (!id) throw new Error('Admin ID is required');
      const response = await getAdminById(id);
      return response;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Create admin mutation hook
 */
export const useCreateAdmin = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: API.CreateAdminParams) => {
      return createAdmin(data);
    },
    onSuccess: (response) => {
      // Invalidate and refetch admins list
      queryClient.invalidateQueries({ queryKey: ['admins'] });
      message.success(
        response.message ||
          intl.formatMessage({ id: 'pages.administrators.createSuccess' }),
      );
    },
    onError: (error: any) => {
      // console.error('❌ Create admin error:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        intl.formatMessage({ id: 'pages.administrators.createFailed' });
      message.error(errorMessage);
    },
  });
};

/**
 * Update admin mutation hook
 */
export const useUpdateAdmin = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: API.UpdateAdminParams;
    }) => {      
      // Verify token is available before making request
      const token = getAuthToken();
      if (!token) {
        // console.error('❌ No auth token found! Please login again.');
        throw new Error('No authentication token found. Please login again.');
      }
      
      return updateAdmin(id, data);
    },
    onSuccess: (response, variables) => {
      // Invalidate and refetch admins list and specific admin
      queryClient.invalidateQueries({ queryKey: ['admins'] });
      queryClient.invalidateQueries({ queryKey: ['admin', variables.id] });
      message.success(
        response.message ||
          intl.formatMessage({ id: 'pages.administrators.updateSuccess' }),
      );
    },
    onError: (error: any) => {
      // console.error('❌ Update admin error:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        intl.formatMessage({ id: 'pages.administrators.updateFailed' });
      message.error(errorMessage);
    },
  });
};

/**
 * Delete admin mutation hook
 */
export const useDeleteAdmin = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return deleteAdmin(id);
    },
    onSuccess: (response) => {
      // Invalidate and refetch admins list
      queryClient.invalidateQueries({ queryKey: ['admins'] });
      message.success(
        response.message ||
          intl.formatMessage({ id: 'pages.administrators.deleteSuccess' }),
      );
    },
    onError: (error: any) => {
      // console.error('❌ Delete admin error:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        intl.formatMessage({ id: 'pages.administrators.deleteFailed' });
      message.error(errorMessage);
    },
  });
};

